import React, { useEffect, useState } from "react";
import { Link, Redirect } from "react-router-dom";
import { Box, Button, Theme, Typography, Link as MuiLink } from "@mui/material";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Snackbar from "@mui/material/Snackbar";
// eslint-disable-next-line no-duplicate-imports
import makeStyles from "@mui/styles/makeStyles";
// eslint-disable-next-line no-duplicate-imports
import createStyles from "@mui/styles/createStyles";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Alert from "@mui/material/Alert";
import { CSSProperties } from "@mui/styles";
import clsx from "clsx";
import { useFormik } from "formik";
import { encode } from "js-base64";
import { object, SchemaOf, string } from "yup";
import { loginAction } from "src/app-redux/auth/actions/authActions";
import {
  isLogin,
  resetErrorAction,
  selectAuthError,
  selectResetEmail,
  selectStatus,
} from "src/app-redux/auth/authSlice";
import { useAppDispatch, useAppSelector } from "src/app-redux/hooks";
import NJSLogo from "src/assets/images/tfg_mainlogo.svg";
import AddTitleToBroswerTab from "src/components/molecules/AddTitleToBroswerTab";
import { WorkOrderRoute } from "src/modules/work-order/workorder.routes";

const mediumText: CSSProperties = {
  fontWeight: 500,
} as const;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    footNote: {
      display: "block",
      maxWidth: "320px",
      margin: "auto",
      fontSize: "12px",
      color: "#96A4B9",
    },
    forgotPasswordButton: {
      paddingTop: "0",
      fontSize: "12px",
      color: "#488CFB",
      textTransform: "capitalize",
      "&:hover": {
        backgroundColor: "transparent",
        textDecoration: "underline",
      },
    },
    backdrop: {
      zIndex: theme.zIndex.modal + 1,
      color: "#fff",
    },
    headerLogo: {
      textAlign: "center",
    },
    headerLogoLink: {
      textAlign: "center",
      width: "100%",
      float: "left",
    },
  })
);

interface IForm {
  password: string;
  email: string;
}
const validationSchema: SchemaOf<IForm> = object({
  email: string().required("Email is required"),
  password: string()
    .max(256, "Password should not exceed more than 256 characters")
    .required("Password is required"),
});
export function LoginPage() {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(resetErrorAction());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const authStatus = useAppSelector(selectStatus);
  const resetEmail = useAppSelector(selectResetEmail);
  const authError = useAppSelector(selectAuthError);
  const user = useAppSelector(isLogin);

  const [state, setState] = useState({ showPassword: false });
  const [alert1, setAlert] = useState(Boolean(authStatus === "failed" && authError));
  useEffect(() => {
    if (authStatus === "failed" && authError) setAlert(true);
    if (!authError) setAlert(false);
  }, [authStatus, authError]);
  const handleClose = (event?: Event | React.SyntheticEvent<any, Event>, reason?: string) => {
    if (reason === "clickaway") return;
    setAlert(false);
  };
  const handleClickShowPassword = () => {
    setState({ ...state, showPassword: !state.showPassword });
  };
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };
  const formik = useFormik<IForm>({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const model = {
        email: values.email,
        password: values.password,
        newPassword: "",
      };
      await dispatch(loginAction(model));
    },
  });
  return authStatus === "passwordResetRequired" && resetEmail ? (
    <Redirect
      to={{ pathname: "/auth/set-password", search: `?email=${encode(resetEmail || "")}` }}
    />
  ) : user ? (
    <Redirect to={{ pathname: WorkOrderRoute.path }} />
  ) : (
    <>
      <AddTitleToBroswerTab title={"TFG | Portal | Login"} />
      <Backdrop className={classes.backdrop} open={Boolean(authStatus === "loading")}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={alert1}
        autoHideDuration={4000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert variant="filled" severity="error">
          Invalid credentials. Please try again.
          <IconButton size="small" aria-label="close" color="inherit" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Alert>
      </Snackbar>
      <div className="login-page">
        <div className="left-section">
          <div className="inner">
            <Box my={2} className="headerLogo" textAlign="center">
              <a href="/" className="headerLogoLink">
                <img src={NJSLogo} alt="logo" width="200" />
              </a>
            </Box>
            <div className="form-wrapper">
              <h2 className="form-heading">Log in to your account</h2>
              <form onSubmit={formik.handleSubmit}>
                <TextField
                  size="small"
                  fullWidth
                  variant="outlined"
                  // type="email"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  label="Email"
                  name="email"
                  // placeholder="myemail@address.com"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />
                <Box m={4} />
                <TextField
                  size="small"
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  label="Password"
                  name="password"
                  type={state.showPassword ? "text" : "password"}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  error={formik.touched.password && Boolean(formik.errors.password)}
                  helperText={formik.touched.password && formik.errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          size="large"
                        >
                          {state.showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Box textAlign={"center"} mt={2}>
                  <Button type="submit" variant="contained" color="secondary" fullWidth>
                    Log In
                  </Button>
                </Box>
                <Box textAlign={"center"} mt={2}>
                  <MuiLink component={Link} to={"/auth/forgot"}>
                    Forgotten password?
                  </MuiLink>
                </Box>
              </form>
            </div>
            <Typography
              component="span"
              variant="body1"
              color="textPrimary"
              style={mediumText}
              className={clsx(classes.footNote)}
            >
              Copyright©, The Facilities Group, {new Date().getFullYear()}
            </Typography>
          </div>
        </div>
        <div className="right-section">
          <div className="login-bg-pattern"></div>
        </div>
      </div>
    </>
  );
}
